// Copyright © 2017-2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <v-menu
    v-model="showMenu"
    transition="scale-transition"
    offset-y
    full-width
    max-width="350px"
    min-width="290px"
    :close-on-content-click="false"
    lazy
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        v-on="on"
        slot="activator"
        v-model="date"
        @click:append="clearValue"
        :append-icon="appendIcon"
        :disabled="disabled"
        hide-details
        :label="label"
        readonly
      ></v-text-field>
    </template>
    <v-card>
      <v-card-title class="subheading">
        {{ outputFormatted }}
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md style="padding: 0px !important;">
          <v-layout row>
            <v-flex>
              <v-text-field
              ref="year"
              :value="year"
              @input="yearInput"
              @blur="updateAll"
              @focus="$event.target.select()"
              @keydown="yearKeydown"
              @mousewheel.capture="yearMousewheel"
              label="Year"
              mask="####"
              style="width:55px;">
              </v-text-field>
            </v-flex>
            <v-flex>
              <v-text-field
              ref="month"
              :value="month"
              @input="monthInput"
              @blur="updateAll"
              @focus="$event.target.select()"
              @keydown="monthKeydown"
              @mousewheel.capture="monthMousewheel"
              label="Month"
              mask="##"
              style="width:40px;">
              </v-text-field>
            </v-flex>
            <v-flex>
              <v-text-field
              ref="day"
              :value="day"
              @input="dayInput"
              @blur="updateAll"
              @focus="$event.target.select()"
              @keydown="dayKeydown"
              @mousewheel.capture="dayMousewheel"
              label="Day"
              mask="##"
              style="width:40px;">
              </v-text-field>
            </v-flex>
            <v-flex xs1>
            </v-flex>

             <v-flex>
              <v-text-field
              ref="hour"
              :value="hour"
              @input="hourInput"
              @blur="updateAll"
              @focus="$event.target.select()"
              @keydown="hourKeydown"
              @mousewheel.capture="hourMousewheel"
              label="Time"
              mask="##"
              style="width:30px;">
              </v-text-field>
            </v-flex>
            <v-flex>
              <p
              class="subheading"
              style="margin-top: 21px;">:
              </p>
            </v-flex>
            <v-flex>
              <v-text-field
              ref="minute"
              :value="minute"
              @input="minuteInput"
              @blur="updateAll"
              @focus="$event.target.select()"
              @keydown="minuteKeydown"
              @mousewheel.capture="minuteMousewheel"
              mask="##"
              style="width:25px;">
              </v-text-field>
            </v-flex>
            <v-flex>
              <v-text-field
              ref="ampm"
              :value="ampm"
              @keydown="ampmKeydown"
              @click="ampmToggle"
              mask="AA"
              readonly
              style="width:25px;">
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="blue" @click="showMenu = false">Cancel</v-btn>
        <v-btn depressed dark color="blue" @click="save()">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import moment from 'moment'

const KEYCODE_ENTER = 13
const UP = 38
const DOWN = 40

export default {
  props: {
    // Allows clearing the date
    clear: Boolean,
    // disable inputs to this selector
    disabled: Boolean,
    // label - text input label
    label: String,
    // v-model input
    value: null
  },

  data () {
    return {
      ampm: '',
      date: this.displayDate(this.value),
      day: '0',
      hour: '0',
      minute: '0',
      month: '0',
      year: '0', // this.getYear(this.value),
      showMenu: false,
      local: this.getMoment(this.value)
    }
  },

  created: function () {
    this.updateAll()
  },

  computed: {
    appendIcon: function () {
      if (this.$props.clear) {
        return 'clear'
      }
      return ''
    },

    outputFormatted: function () {
      return this.local.format('dddd, MMMM Do, YYYY, hh:mm A')
    }
  },

  watch: {
    value (val) {
      this.showMenu = false
      this.date = this.displayDate(this.value)
      this.local = this.getMoment(this.value)
      this.updateAll()
    }
  },

  methods: {
    // functions for things besides updating the date values
    clearValue: function () {
      this.local = moment()
      this.date = null
      this.$emit('input', null)
    },

    displayDate: function (date) {
      if (date) {
        return this.getMoment(date).format('YYYY-MM-DD hh:mm A')
      }
      return date
    },

    getMoment: function (value) {
      if (value) {
        var v = moment(value)
        if (v.isValid() && !v.isBefore('1000-01-01')) {
          return moment(value)
        }
      }
      return moment()
    },

    save: function () {
      this.date = this.local
      this.$emit('input', this.local.format())
    },

    updateAll: function () {
      this.ampm = this.local.format('A')
      this.day = this.local.format('DD')
      this.hour = this.local.format('hh')
      this.minute = this.local.format('mm')
      this.month = this.local.format('MM')
      this.year = this.local.format('YYYY')
    },

  // functions for updating the date values
    ampmKeydown: function () {
      if (event.keyCode === UP || event.keyCode === DOWN) {
        this.ampmToggle()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    ampmToggle: function () {
      if (this.ampm === 'AM') {
        this.local = moment(this.local).add(12, 'hours')
      } else {
        this.local = moment(this.local).add(-12, 'hours')
      }
      this.updateAll()
    },

    dayInput: function (value) {
      if (value > 31) {
        value = 31
      } else if (value < 1) {
        value = 1
      }

      this.local = moment(this.local).date(value)
      if (value.length > 1) {
        this.$refs.hour.focus()
      }
    },

    dayKeydown: function (event) {
      if (event.keyCode === UP) {
        this.local = moment(this.local).add(1, 'day')
        this.updateAll()
      } else if (event.keyCode === DOWN) {
        this.local = moment(this.local).add(-1, 'day')
        this.updateAll()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    dayMousewheel: function (event) {
      if (event.deltaY > 0) {
        this.local = moment(this.local).add(-1, 'day')
        this.updateAll()
      } else if (event.deltaY < 0) {
        this.local = moment(this.local).add(1, 'day')
        this.updateAll()
      }
    },

    hourInput: function (value) {
      if (value > 12) {
        value = 12
      } else if (value < 1) {
        value = 1
      }

      this.local = moment(this.local).hour(value)
      if (value.length > 1) {
        this.$refs.minute.focus()
      }
    },

    hourKeydown: function (event) {
      if (event.keyCode === UP) {
        this.local = moment(this.local).add(1, 'hour')
        this.updateAll()
      } else if (event.keyCode === DOWN) {
        this.local = moment(this.local).add(-1, 'hour')
        this.updateAll()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    hourMousewheel: function (event) {
      if (event.deltaY > 0) {
        this.local = moment(this.local).add(-1, 'hour')
        this.updateAll()
      } else if (event.deltaY < 0) {
        this.local = moment(this.local).add(1, 'hour')
        this.updateAll()
      }
    },

    minuteInput: function (value) {
      if (value > 59) {
        value = 59
      }

      this.local = moment(this.local).minute(value)
      if (value.length > 1) {
        this.$refs.ampm.focus()
      }
    },

    minuteKeydown: function (event) {
      if (event.keyCode === UP) {
        this.local = moment(this.local).add(1, 'minute')
        this.updateAll()
      } else if (event.keyCode === DOWN) {
        this.local = moment(this.local).add(-1, 'minute')
        this.updateAll()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    minuteMousewheel: function (event) {
      if (event.deltaY > 0) {
        this.local = moment(this.local).add(-1, 'minute')
        this.updateAll()
      } else if (event.deltaY < 0) {
        this.local = moment(this.local).add(1, 'minute')
        this.updateAll()
      }
    },

    monthInput: function (value) {
      if (value > 12) {
        value = 12
      } else if (value < 1) {
        value = 1
      }

      this.local = moment(this.local).month((Number(value) - 1))
      if (value.length > 1) {
        this.$refs.day.focus()
      }
    },

    monthKeydown: function (event) {
      if (event.keyCode === UP) {
        this.local = moment(this.local).add(1, 'month')
        this.updateAll()
      } else if (event.keyCode === DOWN) {
        this.local = moment(this.local).add(-1, 'month')
        this.updateAll()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    monthMousewheel: function (event) {
      if (event.deltaY > 0) {
        this.local = moment(this.local).add(-1, 'month')
        this.updateAll()
      } else if (event.deltaY < 0) {
        this.local = moment(this.local).add(1, 'month')
        this.updateAll()
      }
    },

    yearInput: function (value) {
      if (value < 1) {
        value = 1
      }

      this.local = moment(this.local).year(value)
      if (value.length > 3) {
        this.$refs.month.focus()
      }
    },

    yearKeydown: function (event) {
      if (event.keyCode === UP) {
        this.local = moment(this.local).add(1, 'year')
        this.updateAll()
      } else if (event.keyCode === DOWN) {
        this.local = moment(this.local).add(-1, 'year')
        this.updateAll()
      } else if (event.keyCode === KEYCODE_ENTER) {
        this.save()
      }
    },

    yearMousewheel: function (event) {
      if (event.deltaY > 0) {
        this.local = moment(this.local).add(-1, 'year')
        this.updateAll()
      } else if (event.deltaY < 0) {
        this.local = moment(this.local).add(1, 'year')
        this.updateAll()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// DateTimeView.vue?29151b82